import { Card, cn } from "@patient-watch/ui";
import { Label } from "@patient-watch/ui/label";
import type { ReactNode } from "react";
import { OptionalMark } from "./FormDisplayContext";
import { RichLabel } from "./RichLabel";

interface Props {
    /** Id on the question label. Pass the same id to the options so they are labelled by it. */
    labelId?: string;
    caption?: string | null;
    label?: string | null;
    required?: boolean;
    description?: string | null;
    /** Colours the question label red, as FormLabel does when the field has an error. */
    invalid?: boolean;
    children: ReactNode;
}

/**
 * The card every option question sits in: single choice, multiple choice, yes/no, checkboxes,
 * dropdowns, ratings and stars. Caption, question label, required mark and description come first,
 * then the control. Needs no form context, so it can wrap a control that is its own FormField.
 */
export function ChoiceCard({ labelId, caption, label, required = false, description, invalid, children }: Props) {
    const captionText = caption?.trim() ?? "";
    const questionLabel = label?.trim() ?? "";

    return (
        <Card data-choice-question="" className="flex flex-col gap-2 bg-white/70 p-4">
            {captionText ? (
                <span className="block font-normal text-muted-foreground text-lg italic">{captionText}</span>
            ) : null}
            {questionLabel ? (
                <Label id={labelId} className={cn(invalid && "text-red-500 dark:text-red-300")}>
                    <RichLabel>{questionLabel}</RichLabel>
                    {!required ? <OptionalMark /> : null}
                </Label>
            ) : null}
            {description ? (
                <p className="text-slate-500 dark:text-slate-300 text-lg">
                    <RichLabel>{description}</RichLabel>
                </p>
            ) : null}
            {children}
        </Card>
    );
}
